import { IUserProfile } from '../providers/user-provider';
import { Client } from 'graphql-sse';
import { UserDetailsDto } from './userProfile';

export type LoginProgress = {
  stepName: string;
  percentCompleted: number;
  statusCode: number;
};

export enum LoginByGuidPhase {
  INITIALIZING = 'INITIALIZING',
  FETCHING_DETAILS = 'FETCHING_DETAILS',
  VERIFYING_DETAILS = 'VERIFYING_DETAILS',
  FETCHING_BENEFITS = 'FETCHING_BENEFITS',
  FETCHING_PRESCRIPTIONS = 'FETCHING_PRESCRIPTIONS',
  COMPLETED = 'COMPLETED',
  PARTIAL_CONTENT = 'PARTIAL_CONTENT',
  ERROR = 'ERROR',
}

export type LoginByGuidSsePayload = {
  loginByGuidSse: {
    phase: LoginByGuidPhase;
    progress?: LoginProgress;
    userdata?: IUserProfile;
  };
};

export default class LoginService {
  visionSseClient: Client;

  constructor(visionSseClient: Client) {
    this.visionSseClient = visionSseClient;
  }

  subscribeLoginByGuid(
    params: UserDetailsDto,
    onNext: (payload: LoginByGuidSsePayload) => void,
    onError: (error: unknown) => void,
    onComplete: () => void,
  ) {
    return this.visionSseClient.subscribe(
      {
        query: LOGIN_BY_GUID_SSE_SUBSCRIPTION,
        variables: { forceCacheReload: params.forceCacheReload },
      },
      {
        next: result => {
          if ('errors' in result && result.errors) {
            onError(result.errors);
          } else {
            onNext(result.data as LoginByGuidSsePayload);
          }
        },
        error: onError,
        complete: onComplete,
      },
    );
  }
}

// Subscription with progress + the same data shape as BASE_USER_QUERY
export const LOGIN_BY_GUID_SSE_SUBSCRIPTION = `
  fragment RxFields on Prescription {
    status
    type
    eyeglassRxType
    prescriptionId
    issueDate
    expirationDate
    prescribedBy
    prescriptionStrength
    prescriptionId
    leftEye {
      add
      axis
      bc
      color
      cyl
      dia
      model
      pd
      prisms {
        base
        prism
      }
      sku
      sph
      skus {
        packSize
        colour
        sku
        webEnabled
      }
    }
    rightEye {
      add
      axis
      bc
      color
      cyl
      dia
      model
      pd
      prisms {
        base
        prism
      }
      sku
      sph
      skus {
        packSize
        colour
        sku
        webEnabled
      }
    }
  }

  subscription ($forceCacheReload: Boolean!) {
    loginByGuidSse {
      phase
      progress {
        stepName
        percentCompleted
        statusCode
      }
      userdata {
        guid
        mrn
        region
        lastName
        firstName
        dob
        isA2Error
        isNewSession
        isCartLoaded
        isUDSError
        phone
        ctCustomerId
        ctCustomerEmail
        isBenefitsOutOfSync
        email
        medixPlanTypes
        acuitasHome
        acuitasId
        prescriptions(forceCacheReload: $forceCacheReload) {
          active { ...RxFields }
          expired { ...RxFields }
        }
        liveSession {
        eg_pd {
          distance {
            left
            right
          }
          intermediate {
            left
            right
          }
          near {
            left
            right
          }
          isEmpty
          sourcePD
        }
        eg_rx {
          status
          type
          eyeglassRxType
          prescriptionId
          issueDate
          expirationDate
          prescribedBy
          prescriptionStrength
          prescriptionId
          leftEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
              base
              prism
            }
            sku
            sph
          }
          rightEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
              base
              prism
            }
            sku
            sph
          }
        }
        cl_rx {
          status
          type
          eyeglassRxType
          prescriptionId
          issueDate
          expirationDate
          prescribedBy
          prescriptionStrength
          prescriptionId
          leftEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
              base
              prism
            }
            skus {
              packSize
              colour
              sku
              webEnabled
            }
            sku
            sph
          }
          rightEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
              base
              prism
            }
            sku
            skus {
              packSize
              colour
              sku
              webEnabled
            }
            sph
          }
        }
      }
      }
    }
  }
`;
